import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import CurrencySign from 'components/price/CurrencySign.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCurrency } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import currencySign from 'utils/intl/currencySign.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';
import fixed from 'utils/values/fixed.util';

import { DownloadExcelButton } from 'reports/Common/DownloadExcelButton';
import { convertToMoneyAfterVat } from 'reports/utils';

import CN from './BookingValByServiceTable.module.scss';

const BookingValByServiceTable = ({ appointmentsReport, startDate, endDate, currentSign }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const TABLE_HEADERS = useMemo(
    () => [t('businessReports.services.bookingValueTableHeader1'), t('businessReports.services.bookingValueTableHeader2'), t('businessReports.services.bookingValueTableHeader3')],
    [t],
  );

  const response = appointmentsReport?.lastRequestResponse?.service_group;

  const compareServicesByRevenue = (e1, e2) => (e1.total <= e2.total ? 1 : -1);

  const tableData = Object.values(response || {}).sort(compareServicesByRevenue);

  const html = tableData.map((value, index) => (
    <tr key={createNodeKey({ index, tag: value.name })}>
      <td className={CN.firstCell}>{truncateString({ value: value.name, max: 24, suffix: '...' })}</td>
      <td>
        <CurrencySign />
        {fixed(convertToMoneyAfterVat(value.total))}
      </td>
      <td>{value.count}</td>
      <td className={CN.lastCell}></td>
    </tr>
  ));

  const createCsv = useCallback(
    () =>
      asArray(tableData).map((row) => {
        const values = [row.name, currencySign({ value: fixed(convertToMoneyAfterVat(row.total)), currentSalonAbbr: currentSign }), row.count];
        return TABLE_HEADERS.reduce((acc, header, index) => {
          acc[header] = values[index];
          return acc;
        }, {});
      }),
    [tableData, currentSign, TABLE_HEADERS],
  );

  const hasTableData = fromAnyToBool(tableData.length);

  return (
    <div
      data-bem="BookingValByServiceTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>{t('businessReports.services.bookingValueTableTitle')}</div>
        <DownloadExcelButton getCsvData={createCsv} fileName={t('businessReports.services.bookingValueFileName')} disabled={!hasTableData} />
      </div>

      <div className={CN.timeContainer}>
        <div>
          {t('businessReports.services.fromDate')} <span className={CN.bold}>{startDate}</span>
        </div>
        <div>
          <span className={CN.gap}>{t('businessReports.services.toDate')}</span> <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="BookingValByServiceTable">
          <thead>
            <tr>
              <th className={CN.firstCell}>{t('businessReports.services.bookingValueTableHeader1')}</th>
              <th>{t('businessReports.services.bookingValueTableHeader2')}</th>
              <th>{t('businessReports.services.bookingValueTableHeader3')}</th>
              <th className={CN.lastCell}></th>
            </tr>
          </thead>
          <tbody>{html}</tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appointmentsReport: state?.reports?.appointmentsReport,
  currentSign: selectCurrentSalonCurrency(state),
});

export default connect(mapStateToProps)(BookingValByServiceTable);
